import React from 'react';

import { ChatIcon, DollarIcon } from '@sharedCR/coreDesignSystem/icons';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import _ from 'lodash';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { PostClosingOptionListType } from 'constants/postClosingOptions';
import { StatusColor, StatusEnum } from 'constants/status';
import { alwaysTrue, getConditionalArray, getConditionalObject, getValueOrDash } from 'utils/data';
import { formatPercentValue } from 'utils/format';
import { getCombinedPendingChangeKind } from 'utils/pendingChangeKind';
import { getPendingChangeKind } from 'utils/pendingChanges';
import {
  getFieldPendingChangeAuthors,
  getFieldsPendingChangeAuthors,
} from 'utils/pendingChangesAuthors';

import { SERVICING_STATUS_LABEL_VARIANT, servicingStatusOptions } from './PostClosing.constants';
import { PostClosingTestIds } from './PostClosing.testIds';
import {
  formatBoolValue,
  formatCurrency,
  formatDateValue,
  formatLocalDateValue,
  formatLocalStampedValueByUser,
  formatStampedValueByUser,
  getOptionNameByValue,
  getOptionNameOrValue,
} from './PostClosing.utils';
import { getTrackingInfo } from './trackingNumber.utils';

export const investorEditConfigFields = {
  investorId: {
    fieldId: 'investorId',
    type: FieldType.SELECT,
    nameInModel: 'investorId',
    inputProps: {
      label: 'Investor',
      variant: 'outlined',
      options: [],
      required: true,
      widthInColumns: 4,
    },
  },
  dueDate: {
    fieldId: 'dueDate',
    type: FieldType.DATE,
    nameInModel: 'dueDate',
    inputProps: {
      label: 'Due Date',
      variant: 'outlined',
      format: COMMON_DATE_FORMAT,
      dataTestId: PostClosingTestIds.INVESTOR_EDIT_FORM_DUE_DATE_INPUT,
      required: false,
      widthInColumns: 4,
    },
  },
  investorLoanNumber: {
    fieldId: 'investorLoanNumber',
    type: FieldType.INPUT,
    nameInModel: 'investorLoanNumber',
    inputProps: {
      type: FieldInputType.TEXT,
      label: 'Investor Loan Number',
      variant: 'outlined',
      required: false,
      widthInColumns: 4,
    },
  },
  conditions: {
    fieldId: 'conditions',
    type: FieldType.INPUT,
    nameInModel: 'conditions',
    inputProps: {
      type: FieldInputType.TEXT,
      label: 'Conditions',
      variant: 'outlined',
      required: false,
      widthInColumns: 4,
    },
  },
  commitmentNumber: {
    fieldId: 'commitmentNumber',
    type: FieldType.INPUT,
    nameInModel: 'commitmentNumber',
    inputProps: {
      type: FieldInputType.TEXT,
      label: 'Commitment Number',
      variant: 'outlined',
      required: false,
      widthInColumns: 4,
    },
  },
};

export const getOverviewConfig = ({
  postClosing,
  pendingChangesData,
  fieldAuthors = null,
  principalAndInterest,
  monthlyEscrow,
  isUnsecured = false,
}) => {
  const monthlyPayment =
    !_.isNil(principalAndInterest) || !_.isNil(monthlyEscrow)
      ? (principalAndInterest ?? 0) + (monthlyEscrow ?? 0)
      : null;

  return [
    {
      name: 'Principal Balance',
      value: formatCurrency(postClosing?.principalBalance),
      icon: <DollarIcon />,
      iconColor: StatusColor[StatusEnum.SUCCESS],
      changeKind: getCombinedPendingChangeKind(
        [
          pendingChangesData?.principalBalance,
          pendingChangesData?.totalLoanAmount,
          pendingChangesData?.loanAmount,
        ].map(getPendingChangeKind),
      ),
      authors: getFieldsPendingChangeAuthors(fieldAuthors, [
        'principalBalance',
        'totalLoanAmount',
        'loanAmount',
      ]),
    },
    {
      name: 'Monthly Payment',
      value: formatCurrency(monthlyPayment),
      icon: <DollarIcon />,
      iconColor: StatusColor[StatusEnum.INFO],
      changeKind: getPendingChangeKind(pendingChangesData?.principalAndInterest),
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'principalAndInterest'),
    },
    ...getConditionalArray(!isUnsecured, () => [
      {
        name: 'Escrow Balance',
        value: formatCurrency(postClosing?.escrowBalance),
        icon: <DollarIcon />,
        iconColor: StatusColor[StatusEnum.INFO],
        changeKind: getPendingChangeKind(pendingChangesData?.escrowBalance),
        authors: getFieldPendingChangeAuthors(fieldAuthors, 'escrowBalance'),
      },
    ]),
    {
      name: 'Servicer',
      value: postClosing?.servicer ?? '-',
      icon: <ChatIcon />,
      iconColor: StatusColor[StatusEnum.WARNING],
      changeKind: getPendingChangeKind(pendingChangesData?.servicer),
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'servicer'),
    },
  ];
};

const renderServicingStatusLabel = (servicingStatus, title) => {
  if (_.isNil(servicingStatus)) {
    return null;
  }

  const variant = SERVICING_STATUS_LABEL_VARIANT[servicingStatus];

  return <LabelCDS size={LabelSize.S} title={title} variant={variant} />;
};

export const getInvestorSummaryConfig = ({
  postClosing,
  investorName,
  pendingChangesData,
  fieldAuthors = null,
  isUnsecured = false,
}) => ({
  title: 'Investor',
  name: investorName ?? '-',
  icon: <ChatIcon />,
  iconColor: 'primary',
  isModified: !!pendingChangesData?.investorId,
  authors: getFieldPendingChangeAuthors(fieldAuthors, 'investorId'),
  fields: [
    {
      id: 'investorLoanNumber',
      name: 'Investor Loan Number',
      value: postClosing?.investorLoanNumber ?? '-',
      isModified: !!pendingChangesData?.investorLoanNumber,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'investorLoanNumber'),
    },
    ...getConditionalArray(!isUnsecured, () => [
      {
        id: 'commitmentNumber',
        name: 'Commitment Number',
        value: postClosing?.commitmentNumber ?? '-',
        isModified: !!pendingChangesData?.commitmentNumber,
        authors: getFieldPendingChangeAuthors(fieldAuthors, 'commitmentNumber'),
      },
      {
        id: 'poolNumber',
        name: 'Pool Number',
        value: postClosing?.poolNumber ?? '-',
        isModified: !!pendingChangesData?.poolNumber,
        authors: getFieldPendingChangeAuthors(fieldAuthors, 'poolNumber'),
      },
    ]),
    {
      id: 'investorPackageUploadedDate',
      name: 'Package Uploaded Date',
      value: formatDateValue(postClosing?.investorPackageUploadedDate),
      isModified: !!pendingChangesData?.investorPackageUploadedDate,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'investorPackageUploadedDate'),
    },
    {
      id: 'investorPackageUploadedBy',
      name: 'Package Uploaded By',
      value: getValueOrDash(postClosing?.investorPackageUploadedByName),
    },
  ],
});

export const getServicerSummaryConfig = ({
  postClosing,
  pendingChangesData,
  fieldAuthors = null,
}) => {
  const { servicingStatus } = postClosing ?? {};
  const servicingStatusName = getOptionNameByValue(servicingStatusOptions, servicingStatus);
  const servicingStatusNode = renderServicingStatusLabel(servicingStatus, servicingStatusName);

  return {
    title: 'Servicer',
    name: postClosing?.servicer ?? '-',
    icon: <ChatIcon />,
    iconColor: 'secondary',
    isModified: !!pendingChangesData?.servicer,
    authors: getFieldPendingChangeAuthors(fieldAuthors, 'servicer'),
    fields: [
      {
        id: 'servicerLoanNumber',
        name: 'Servicer Loan Number',
        value: postClosing?.servicerLoanNumber ?? '-',
        isModified: !!pendingChangesData?.servicerLoanNumber,
        authors: getFieldPendingChangeAuthors(fieldAuthors, 'servicerLoanNumber'),
      },
      {
        id: 'servicingStatus',
        name: 'Servicing Status',
        value: servicingStatusName,
        valueNode: servicingStatusNode,
        isModified: !!pendingChangesData?.servicingStatus,
        authors: getFieldPendingChangeAuthors(fieldAuthors, 'servicingStatus'),
      },
    ],
  };
};

export const getLoanIdentifiersConfig = ({
  postClosing,
  pendingChangesData,
  fieldAuthors = null,
  isUnsecured = false,
}) => {
  if (isUnsecured) {
    return [];
  }

  return [
    {
      id: 'min',
      name: 'MIN / MERS Number',
      value: postClosing?.min ?? '-',
      isModified: !!pendingChangesData?.min,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'min'),
    },
  ];
};

export const getAccountingGridConfig = ({
  postClosing,
  pendingChangesData,
  fieldAuthors = null,
  isUnsecured = false,
}) => {
  return [
    {
      name: 'Principal Purchased',
      value: formatCurrency(postClosing?.principalPurchased),
      isModified: !!pendingChangesData?.principalPurchased,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'principalPurchased'),
    },
    {
      name: 'Discount/Premium %',
      value: formatPercentValue(postClosing?.discountPremiumPercent) ?? '-',
      isModified: !!pendingChangesData?.discountPremiumPercent,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'discountPremiumPercent'),
    },
    {
      name: 'Discount/Premium $',
      value: formatCurrency(postClosing?.discount),
      isModified: !!pendingChangesData?.discount,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'discount'),
    },
    ...getConditionalArray(!isUnsecured, () => [
      {
        name: 'Funding Fee',
        value: formatCurrency(postClosing?.fundingFee),
        isModified: !!pendingChangesData?.fundingFee,
        authors: getFieldPendingChangeAuthors(fieldAuthors, 'fundingFee'),
      },
      {
        name: 'Tax Fee',
        value: formatCurrency(postClosing?.taxFee),
        isModified: !!pendingChangesData?.taxFee,
        authors: getFieldPendingChangeAuthors(fieldAuthors, 'taxFee'),
      },
      {
        name: 'Escrow Balance',
        value: formatCurrency(postClosing?.escrowBalance),
        isModified: !!pendingChangesData?.escrowBalance,
        authors: getFieldPendingChangeAuthors(fieldAuthors, 'escrowBalance'),
      },
    ]),
    {
      name: 'Interest',
      value: formatCurrency(postClosing?.interest),
      isModified: !!pendingChangesData?.interest,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'interest'),
    },
    {
      name: 'Investor Wire Amount',
      value: formatCurrency(postClosing?.investorWireAmount),
      isModified: !!pendingChangesData?.investorWireAmount,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'investorWireAmount'),
    },
  ];
};

export const getShippingGridConfig = ({
  postClosing,
  pendingChangesData,
  fieldAuthors = null,
  getUserName,
}) => {
  return [
    {
      name: 'Tracking # Title Company to Lender',
      value: postClosing?.trackingNumberTitleCompanyToLender ?? '-',
      isModified: !!pendingChangesData?.trackingNumberTitleCompanyToLender,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'trackingNumberTitleCompanyToLender'),
      trackingInfo: getTrackingInfo(postClosing?.trackingNumberTitleCompanyToLender),
    },
    {
      name: 'Shipped Title Company to Lender Date',
      value: formatDateValue(postClosing?.shippedTitleCompanyToLenderDate),
      isModified: !!pendingChangesData?.shippedTitleCompanyToLenderDate,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'shippedTitleCompanyToLenderDate'),
    },
    {
      name: 'Received Title Company to Lender Date',
      value: formatDateValue(postClosing?.receivedTitleCompanyToLenderDate),
      isModified: !!pendingChangesData?.receivedTitleCompanyToLenderDate,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'receivedTitleCompanyToLenderDate'),
    },
    {
      name: 'Collateral Tracking # to Warehouse',
      value: postClosing?.collateralTrackingNumberToWarehouseBank ?? '-',
      isModified: !!pendingChangesData?.collateralTrackingNumberToWarehouseBank,
      authors: getFieldPendingChangeAuthors(
        fieldAuthors,
        'collateralTrackingNumberToWarehouseBank',
      ),
      trackingInfo: getTrackingInfo(postClosing?.collateralTrackingNumberToWarehouseBank),
    },
    {
      name: 'Collateral Shipped to Warehouse Date',
      value: formatDateValue(postClosing?.collateralShippedToWarehouseDate),
      isModified: !!pendingChangesData?.collateralShippedToWarehouseDate,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'collateralShippedToWarehouseDate'),
    },
    {
      name: 'Collateral Received to Warehouse Date',
      value: formatDateValue(postClosing?.collateralReceivedToWarehouseDate),
      isModified: !!pendingChangesData?.collateralReceivedToWarehouseDate,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'collateralReceivedToWarehouseDate'),
    },
    {
      name: 'Note Tracking # to Warehouse',
      value: postClosing?.noteTrackingNumberToWarehouse ?? '-',
      isModified: !!pendingChangesData?.noteTrackingNumberToWarehouse,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'noteTrackingNumberToWarehouse'),
      trackingInfo: getTrackingInfo(postClosing?.noteTrackingNumberToWarehouse),
    },
    {
      name: 'Note Shipped to Warehouse Date',
      value: formatDateValue(postClosing?.noteShippedToWarehouseDate),
      isModified: !!pendingChangesData?.noteShippedToWarehouseDate,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'noteShippedToWarehouseDate'),
    },
    {
      name: 'Note Received to Warehouse Date',
      value: formatDateValue(postClosing?.noteReceivedToWarehouseDate),
      isModified: !!pendingChangesData?.noteReceivedToWarehouseDate,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'noteReceivedToWarehouseDate'),
    },
    {
      name: 'Collateral Tracking # to Investor',
      value: postClosing?.collateralTrackingNumberToInvestor ?? '-',
      isModified: !!pendingChangesData?.collateralTrackingNumberToInvestor,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'collateralTrackingNumberToInvestor'),
      trackingInfo: getTrackingInfo(postClosing?.collateralTrackingNumberToInvestor),
    },
    {
      name: 'Collateral Shipped to Investor Date',
      value: formatDateValue(postClosing?.collateralShippedToInvestorDate),
      isModified: !!pendingChangesData?.collateralShippedToInvestorDate,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'collateralShippedToInvestorDate'),
    },
    {
      name: 'Collateral Received to Investor Date',
      value: formatDateValue(postClosing?.collateralReceivedToInvestorDate),
      isModified: !!pendingChangesData?.collateralReceivedToInvestorDate,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'collateralReceivedToInvestorDate'),
    },
    {
      name: 'Note Tracking # to Investor',
      value: postClosing?.noteTrackingNumberToInvestor ?? '-',
      isModified: !!pendingChangesData?.noteTrackingNumberToInvestor,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'noteTrackingNumberToInvestor'),
      trackingInfo: getTrackingInfo(postClosing?.noteTrackingNumberToInvestor),
    },
    {
      name: 'Note Shipped to Investor Date / By',
      value: formatStampedValueByUser(
        postClosing?.noteShippedToInvestorDate,
        postClosing?.noteShippedToInvestorByUserId,
        getUserName,
      ),
      isModified: !!pendingChangesData?.noteShippedToInvestorDate,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'noteShippedToInvestorDate'),
    },
    {
      name: 'Note Received to Investor Date',
      value: formatDateValue(postClosing?.noteReceivedToInvestorDate),
      isModified: !!pendingChangesData?.noteReceivedToInvestorDate,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'noteReceivedToInvestorDate'),
    },
    {
      name: 'Note Tracking # from Original Investor',
      value: postClosing?.noteTrackingNumberFromOriginalInvestor ?? '-',
      isModified: !!pendingChangesData?.noteTrackingNumberFromOriginalInvestor,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'noteTrackingNumberFromOriginalInvestor'),
      trackingInfo: getTrackingInfo(postClosing?.noteTrackingNumberFromOriginalInvestor),
    },
    {
      name: 'Original Mortgage Tracking # to Investor',
      value: postClosing?.originalMortgageTrackingNumberToInvestor ?? '-',
      isModified: !!pendingChangesData?.originalMortgageTrackingNumberToInvestor,
      authors: getFieldPendingChangeAuthors(
        fieldAuthors,
        'originalMortgageTrackingNumberToInvestor',
      ),
      trackingInfo: getTrackingInfo(postClosing?.originalMortgageTrackingNumberToInvestor),
    },
  ];
};

export const getPaymentGridConfig = ({ postClosing, pendingChangesData, fieldAuthors = null }) => {
  const autoDebitLabel = postClosing?.autoDebitPayment ? 'Yes' : 'No';
  const autoDebitValue = _.isBoolean(postClosing?.autoDebitPayment) ? autoDebitLabel : '-';

  return [
    {
      name: 'Auto Debit Payment',
      value: autoDebitValue,
      isModified: !!pendingChangesData?.autoDebitPayment,
      authors: getFieldPendingChangeAuthors(fieldAuthors, 'autoDebitPayment'),
    },
  ];
};

export const getFinalDocumentsGridConfig = ({
  postClosing,
  getUserName,
  closingDate,
  firstPaymentDate,
}) => {
  const fd = postClosing?.finalDocuments ?? {};

  return [
    {
      id: 'closingDate',
      name: 'Closing Date',
      value: formatLocalDateValue(closingDate),
      isReadOnly: true,
    },
    {
      id: 'firstPaymentDate',
      name: 'First Payment Date',
      value: formatDateValue(firstPaymentDate),
      isReadOnly: true,
    },
    {
      id: 'closedPackageReceivedDate',
      name: 'Closed Package Received Date',
      value: formatDateValue(fd.closedPackageReceivedDate),
    },
    {
      id: 'noteMissingFromClosedPackage',
      name: 'Note Missing from Closed Package',
      value: formatBoolValue(fd.noteMissingFromClosedPackage),
    },
    {
      id: 'postClosingDueDate',
      name: 'Post-Closing Due Date',
      value: formatDateValue(fd.postClosingDueDate),
      isReadOnly: true,
      withTooltip: true,
    },
    {
      id: 'postClosingReviewCompleted',
      name: 'Post-Closing Review Completed',
      value: formatBoolValue(fd.postClosingReviewCompleted),
    },
    {
      id: 'postClosingReviewCompletedDate',
      name: 'Review Completed Date / By',
      value: formatLocalStampedValueByUser(
        fd.postClosingReviewCompletedDate,
        fd.postClosingReviewCompletedByUserId,
        getUserName,
      ),
    },
    {
      id: 'postClosingReviewDefects',
      name: 'Post-Closing Review Defects',
      value: getValueOrDash(fd.postClosingReviewDefects),
      isFullWidth: true,
    },
    {
      id: 'investorLockDate',
      name: 'Investor Lock Date',
      value: formatDateValue(fd.investorLockDate),
    },
    {
      id: 'investorLockExpiration',
      name: 'Investor Lock Expiration',
      value: formatDateValue(fd.investorLockExpiration),
    },
    {
      id: 'preppedForTransmissionDate',
      name: 'Prepped for Transmission Date / By',
      value: formatStampedValueByUser(
        fd.preppedForTransmissionDate,
        fd.preppedForTransmissionByUserId,
        getUserName,
      ),
    },
    {
      id: 'loanTransmittedDate',
      name: 'Loan Transmitted Date / By',
      value: formatStampedValueByUser(
        fd.loanTransmittedDate,
        fd.loanTransmittedByUserId,
        getUserName,
      ),
    },
    {
      id: 'transmissionConfirmationDate',
      name: 'Transmission Confirmation Date',
      value: formatDateValue(fd.transmissionConfirmationDate),
    },
    {
      id: 'transmissionComments',
      name: 'Transmission Comments',
      value: getValueOrDash(fd.transmissionComments),
      isFullWidth: true,
    },
    {
      id: 'correctionInProcessDate',
      name: 'Correction In Process Date / By',
      value: formatStampedValueByUser(
        fd.correctionInProcessDate,
        fd.correctionInProcessByUserId,
        getUserName,
      ),
    },
    {
      id: 'unrecordedMortgageReceivedDate',
      name: 'Unrecorded Mortgage Received Date / By',
      value: formatStampedValueByUser(
        fd.unrecordedMortgageReceivedDate,
        fd.unrecordedMortgageReceivedByUserId,
        getUserName,
      ),
    },
    {
      id: 'copyOfRecordedMortgageReceivedDate',
      name: 'Copy of Recorded Mortgage Received Date / By',
      value: formatStampedValueByUser(
        fd.copyOfRecordedMortgageReceivedDate,
        fd.copyOfRecordedMortgageReceivedByUserId,
        getUserName,
      ),
    },
    {
      id: 'recordedMortgageUploadedDate',
      name: 'Recorded Mortgage Uploaded Date / By',
      value: formatStampedValueByUser(
        fd.recordedMortgageUploadedDate,
        fd.recordedMortgageUploadedByUserId,
        getUserName,
      ),
    },
    {
      id: 'recordedMortgageRequestedDate',
      name: 'Recorded Mortgage Requested Date / By',
      value: formatStampedValueByUser(
        fd.recordedMortgageRequestedDate,
        fd.recordedMortgageRequestedByUserId,
        getUserName,
      ),
    },
    {
      id: 'originalMortgageDeliveredDate',
      name: 'Original Mortgage Delivered Date / By',
      value: formatStampedValueByUser(
        fd.originalMortgageDeliveredDate,
        fd.originalMortgageDeliveredByUserId,
        getUserName,
      ),
    },
    {
      id: 'recordedMortgageDeliveredToInvestorDate',
      name: 'Recorded Mortgage Delivered to Investor Date / By',
      value: formatStampedValueByUser(
        fd.recordedMortgageDeliveredToInvestorDate,
        fd.recordedMortgageDeliveredToInvestorByUserId,
        getUserName,
      ),
    },
    {
      id: 'recordedMortgageNotes',
      name: 'Recorded Mortgage Notes',
      value: getValueOrDash(fd.recordedMortgageNotes),
      isFullWidth: true,
    },
    {
      id: 'titlePolicyUploadedDate',
      name: 'Title Policy Uploaded Date / By',
      value: formatStampedValueByUser(
        fd.titlePolicyUploadedDate,
        fd.titlePolicyUploadedByUserId,
        getUserName,
      ),
    },
    {
      id: 'mortgagePaidOffTitlePolicyNotRequired',
      name: 'Mortgage Paid Off - Title Policy Not Required',
      value: formatBoolValue(fd.mortgagePaidOffTitlePolicyNotRequired),
    },
    {
      id: 'titlePolicyRequestedDate',
      name: 'Title Policy Requested Date / By',
      value: formatStampedValueByUser(
        fd.titlePolicyRequestedDate,
        fd.titlePolicyRequestedByUserId,
        getUserName,
      ),
    },
    {
      id: 'titlePolicyReceivedDate',
      name: 'Title Policy Received Date / By',
      value: formatStampedValueByUser(
        fd.titlePolicyReceivedDate,
        fd.titlePolicyReceivedByUserId,
        getUserName,
      ),
    },
    {
      id: 'titlePolicyCorrectionInProcessDate',
      name: 'Title Policy Correction In Process Date / By',
      value: formatStampedValueByUser(
        fd.titlePolicyCorrectionInProcessDate,
        fd.titlePolicyCorrectionInProcessByUserId,
        getUserName,
      ),
    },
    {
      id: 'titlePolicyDeliveredToInvestorDate',
      name: 'Title Policy Delivered to Investor Date / By',
      value: formatStampedValueByUser(
        fd.titlePolicyDeliveredToInvestorDate,
        fd.titlePolicyDeliveredToInvestorByUserId,
        getUserName,
      ),
    },
    {
      id: 'titlePolicyNotes',
      name: 'Title Policy Notes',
      value: getValueOrDash(fd.titlePolicyNotes),
      isFullWidth: true,
    },
    {
      id: 'tpMtgInFileCabinetDate',
      name: 'TP Mtg In File Cabinet Date / By',
      value: formatStampedValueByUser(
        fd.tpMtgInFileCabinetDate,
        fd.tpMtgInFileCabinetByUserId,
        getUserName,
      ),
    },
    {
      id: 'noteReturnedDate',
      name: 'Note Returned Date / By',
      value: formatStampedValueByUser(fd.noteReturnedDate, fd.noteReturnedByUserId, getUserName),
    },
    {
      id: 'noteReturnedReason',
      name: 'Note Returned Reason',
      value: fd.noteReturnedReason ?? '-',
    },
    {
      id: 'finalDocumentsComments',
      name: 'Final Documents Comments',
      value: fd.finalDocumentsComments ?? '-',
      isFullWidth: true,
    },
  ];
};

export const getMersGridConfig = ({ postClosing, getUserName, optionsByListType = {} }) => {
  const mers = postClosing?.mersInformation ?? {};

  return [
    {
      id: 'mersTransferBatchNumber',
      name: 'MERS Transfer Batch Number',
      value: getValueOrDash(mers.mersTransferBatchNumber),
    },
    {
      id: 'mersTransferDate',
      name: 'MERS Transfer Date / By',
      value: formatStampedValueByUser(
        mers.mersTransferDate,
        mers.mersTransferByUserId,
        getUserName,
      ),
    },
    {
      id: 'mersTransferAcceptedDate',
      name: 'MERS Transfer Accepted Date / By',
      value: formatStampedValueByUser(
        mers.mersTransferAcceptedDate,
        mers.mersTransferAcceptedByUserId,
        getUserName,
      ),
    },
    {
      id: 'minRegistrationReversedDate',
      name: 'MIN Registration Reversed Date / By',
      value: formatStampedValueByUser(
        mers.minRegistrationReversedDate,
        mers.minRegistrationReversedByUserId,
        getUserName,
      ),
    },
    {
      id: 'minDeactivatedDate',
      name: 'MIN Deactivated Date / By',
      value: formatStampedValueByUser(
        mers.minDeactivatedDate,
        mers.minDeactivatedByUserId,
        getUserName,
      ),
    },
    {
      id: 'minDeactivationType',
      name: 'MIN Deactivation Type',
      value: getOptionNameOrValue(
        optionsByListType[PostClosingOptionListType.MIN_DEACTIVATION_TYPE],
        mers.minDeactivationType,
      ),
    },
    {
      id: 'minRegistrationReversalReason',
      name: 'MIN Registration Reversal Reason',
      value: getValueOrDash(mers.minRegistrationReversalReason),
      isFullWidth: true,
    },
  ];
};

export const getFlippedUnsalableGridConfig = ({
  postClosing,
  getUserName,
  investorName,
  optionsByListType = {},
}) => {
  const flipped = postClosing?.flippedUnsalable ?? {};

  return [
    {
      id: 'investorName',
      name: 'Investor',
      value: getValueOrDash(investorName),
      isReadOnly: true,
    },
    {
      id: 'investorLoanNumber',
      name: 'Investor Loan Number',
      value: getValueOrDash(postClosing?.investorLoanNumber),
      isReadOnly: true,
    },
    {
      id: 'investorLockDate',
      name: 'Investor Lock Date',
      value: formatDateValue(postClosing?.finalDocuments?.investorLockDate),
      isReadOnly: true,
    },
    {
      id: 'investorLockExpiration',
      name: 'Investor Lock Expiration',
      value: formatDateValue(postClosing?.finalDocuments?.investorLockExpiration),
      isReadOnly: true,
    },
    {
      id: 'flippedToNewInvestorDate',
      name: 'Flipped to New Investor Date / By',
      value: formatStampedValueByUser(
        flipped.flippedToNewInvestorDate,
        flipped.flippedToNewInvestorByUserId,
        getUserName,
      ),
    },
    {
      id: 'markedUnsalableDate',
      name: 'Marked Unsalable Date / By',
      value: formatStampedValueByUser(
        flipped.markedUnsalableDate,
        flipped.markedUnsalableByUserId,
        getUserName,
      ),
    },
    {
      id: 'noteReturnRequestedDate',
      name: 'Note Return Requested Date',
      value: formatDateValue(flipped.noteReturnRequestedDate),
    },
    {
      id: 'unsalableResponsibleParty',
      name: 'Unsalable Responsible Party',
      value: getOptionNameOrValue(
        optionsByListType[PostClosingOptionListType.UNSALABLE_RESPONSIBLE_PARTY],
        flipped.unsalableResponsibleParty,
      ),
    },
    {
      id: 'unsalableReason',
      name: 'Unsalable Reason',
      value: getOptionNameOrValue(
        optionsByListType[PostClosingOptionListType.UNSALABLE_REASON],
        flipped.unsalableReason,
      ),
    },
    {
      id: 'purchaseDenialReason',
      name: 'Purchase Denial Reason',
      value: getValueOrDash(flipped.purchaseDenialReason),
      isFullWidth: true,
    },
  ];
};

export const getShipperMilestoneGridConfig = ({ postClosing, getUserName }) => {
  const sm = postClosing?.shipperMilestone ?? {};

  return [
    {
      id: 'closingPackageUploaded',
      name: 'Closing Package Uploaded',
      value: formatBoolValue(sm.closingPackageUploaded),
    },
    {
      id: 'closingPackageUploadedDate',
      name: 'Closing Package Uploaded Date / By',
      value: formatLocalStampedValueByUser(
        sm.closingPackageUploadedDate,
        sm.closingPackageUploadedByUserId,
        getUserName,
      ),
    },
    {
      id: 'creditPackageUploaded',
      name: 'Credit Package Uploaded',
      value: formatBoolValue(sm.creditPackageUploaded),
    },
    {
      id: 'creditPackageUploadedDate',
      name: 'Credit Package Uploaded Date / By',
      value: formatLocalStampedValueByUser(
        sm.creditPackageUploadedDate,
        sm.creditPackageUploadedByUserId,
        getUserName,
      ),
    },
  ];
};

export const getDocumentsTableConfig = ({ isUnsecured = false } = {}) => ({
  documentType: {
    label: 'Document',
    width: isUnsecured ? '35%' : '20%',
  },
  receivedDate: {
    label: 'Received Date',
    width: isUnsecured ? '20%' : '15%',
    content: row => formatDateValue(row.receivedDate),
    renderContentAsText: alwaysTrue,
  },
  toInvestorDate: {
    label: 'To Investor',
    width: isUnsecured ? '20%' : '15%',
    content: row => formatDateValue(row.toInvestorDate),
    renderContentAsText: alwaysTrue,
  },
  ...getConditionalObject(!isUnsecured, {
    documentRecordingAmount: {
      label: 'Recording Amount On Document',
      width: '20%',
      content: row => formatCurrency(row.documentRecordingAmount),
      renderContentAsText: alwaysTrue,
    },
    finalCdRecordingAmount: {
      label: 'Recording Amount From Final CD',
      width: '20%',
      content: row => formatCurrency(row.finalCdRecordingAmount),
      renderContentAsText: alwaysTrue,
    },
  }),
  comments: {
    label: 'Comments',
    width: isUnsecured ? '25%' : '10%',
  },
});
